import type { CertificationDefinition } from '../models/types';

function isCertLike(value: unknown): value is Partial<CertificationDefinition> {
  return !!value && typeof value === 'object';
}

/** Accepts only `#rgb`, `#rrggbb`, or `#rrggbbaa` hex literals. */
const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/**
 * Coerce an untrusted color string to a safe value. The app's `<input type="color">`
 * pickers and built-in palettes only ever emit hex literals, so any other shape —
 * in particular a string crafted to break out of a `style="…"` attribute, e.g.
 * `#fff"><img src=x onerror=alert(1)>`, which can only arrive via an imported
 * file — collapses to the neutral fallback. This is the single chokepoint that
 * keeps malicious colors out of every `style=`/`value=` render sink, so the many
 * downstream badge/tooltip/board renderers can interpolate `color` directly.
 */
export function sanitizeColor(raw: unknown, fallback = '#7f8c8d'): string {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (HEX_COLOR_RE.test(trimmed)) return trimmed;
  }
  return fallback;
}

/** Validate and deduplicate certification definitions from raw data.
 *  No built-in definitions are force-added — the input is the source of truth. */
export function normalizeCertificationDefinitions(raw: unknown): CertificationDefinition[] {
  const normalized: CertificationDefinition[] = [];
  const seen = new Set<string>();

  const add = (candidate: Partial<CertificationDefinition>): void => {
    const id = typeof candidate.id === 'string' ? candidate.id.trim() : '';
    const label = typeof candidate.label === 'string' ? candidate.label.trim() : '';
    const color = sanitizeColor(candidate.color);
    if (!id || !label || seen.has(id)) return;
    normalized.push({
      id,
      label,
      color,
      ...(candidate.deleted ? { deleted: true } : {}),
    });
    seen.add(id);
  };

  if (Array.isArray(raw)) {
    for (const entry of raw) {
      if (isCertLike(entry)) add(entry);
    }
  }

  return normalized;
}
